import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import PlaybookEditor from '@/components/playbook-editor/PlaybookEditor'
import type { PlaybookDetails } from '@/types/Playbook'
import { useSettings } from '@/contexts/SettingsContext'
import { Loading } from '@/components/ui/Loading'
import { useEntityViewTracking } from '@/hooks/UseActivityTracking'
import { playbookKeys } from '@/api/queries/PlaybookQueries'
import { useEffect, useMemo } from 'react'
import { secureFetch } from '@/utils/SecureFetch'

interface Team {
	id: string
	name: string
}

async function fetchPlaybookDetails(playbookId: string): Promise<{ playbook: PlaybookDetails }> {
	const response = await secureFetch(`/api/playbooks/${playbookId}`)
	if (response.status === 401) throw new Error('UNAUTHORIZED')
	if (response.status === 403) throw new Error('FORBIDDEN')
	if (response.status === 404) throw new Error('NOT_FOUND')
	if (!response.ok) throw new Error('Failed to fetch playbook data')
	return response.json()
}

async function fetchTeams(): Promise<{ teams: Team[] }> {
	const response = await secureFetch('/api/teams')
	if (!response.ok) return { teams: [] }
	return response.json()
}

export function PlaybookEditorPage() {
	const { playbookId } = useParams()
	const navigate = useNavigate()
	const { setLastViewedPlaybookId } = useSettings()

	// Track playbook view duration
	useEntityViewTracking('playbook', playbookId ? playbookId : undefined)

	// Fetch playbook details via React Query
	const {
		data: playbookData,
		isLoading: playbookLoading,
		error: playbookError,
	} = useQuery({
		queryKey: playbookKeys.detail(playbookId ?? ''),
		queryFn: () => fetchPlaybookDetails(playbookId!),
		enabled: !!playbookId,
		retry: false,
	})

	const playbook = playbookData?.playbook ?? null

	// Fetch teams data via React Query
	const { data: teamsData } = useQuery({
		queryKey: ['teams'],
		queryFn: fetchTeams,
		enabled: !!playbook?.team_id,
	})

	// Derive team from teams data
	const team = useMemo(() => {
		if (!playbook?.team_id || !teamsData?.teams) return null
		return teamsData.teams.find(t => t.id === playbook.team_id) ?? null
	}, [playbook?.team_id, teamsData?.teams])

	// Update last viewed playbook when data loads
	useEffect(() => {
		if (playbook?.id) {
			setLastViewedPlaybookId(playbook.id)
		}
	}, [playbook?.id, setLastViewedPlaybookId])

	// Handle auth errors by redirecting
	useEffect(() => {
		if (playbookError?.message === 'UNAUTHORIZED') {
			navigate('/login')
		}
	}, [playbookError, navigate])

	// Derive error message for display
	const errorMessage = playbookError
		? playbookError.message === 'FORBIDDEN'
			? 'You do not have permission to access this playbook'
			: playbookError.message === 'NOT_FOUND'
				? 'Playbook not found'
				: playbookError.message === 'UNAUTHORIZED'
					? null // handled by redirect
					: 'An error occurred'
		: null

	// Navigation handlers
	const handleBack = () => navigate('/content')
	const handleOpenPlay = (playId: string) => {
		navigate(`/playbooks/${playbookId}/play/${playId}`)
	}
	const handleImport = () => {
	}
	const handleExport = () => {
	}

	// Loading state
	if (playbookLoading) {
		return <Loading fullscreen />
	}

	// Error state
	if (errorMessage) {
		return (
			<div className="page-error">
				<div className="page-error-content">
					<h1 className="page-error-title" data-variant="error">Error</h1>
					<p className="page-error-message">{errorMessage}</p>
					<button onClick={handleBack} className="page-error-button">
						Back to Playbooks
					</button>
				</div>
			</div>
		)
	}

	// Not found state
	if (!playbook) {
		return (
			<div className="page-error">
				<div className="page-error-content">
					<h1 className="page-error-title" data-variant="info">Playbook Not Found</h1>
					<p className="page-error-message">The playbook you're looking for doesn't exist.</p>
					<button onClick={handleBack} className="page-error-button">
						Back to Playbooks
					</button>
				</div>
			</div>
		)
	}

	return (
		<PlaybookEditor
			playbookId={playbookId}
			playbookName={playbook.name}
			teamId={playbook.team_id?.toString()}
			teamName={team?.name}
			onBack={handleBack}
			onOpenPlay={handleOpenPlay}
			onImport={handleImport}
			onExport={handleExport}
		/>
	)
}
